
import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { useServices } from "@/hooks/useServices";
import { useAvailableTimeSlots } from "@/hooks/useAvailableTimeSlots";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { Calendar as CalendarIcon, Clock, Check, AlertCircle } from "lucide-react";
import { calculateUTCBookingTimes } from "@/utils/timezoneUtils";

interface CreateBookingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  providerId: string;
  onBookingCreated: () => void;
}

const CreateBookingDialog = ({ open, onOpenChange, providerId, onBookingCreated }: CreateBookingDialogProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [selectedServiceId, setSelectedServiceId] = useState<string>("");
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [selectedTimeSlotId, setSelectedTimeSlotId] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [emailValidation, setEmailValidation] = useState<{
    isValidating: boolean;
    isValid: boolean | null;
    existingUserId: string | null;
  }>({
    isValidating: false,
    isValid: null,
    existingUserId: null,
  });

  const { data: services } = useServices(providerId);
  // FIXED: Pass isProviderViewing=true to use simplified provider logic
  const { data: availableTimeSlots, isLoading: timeSlotsLoading } = useAvailableTimeSlots(
    selectedServiceId, 
    selectedDate,
    true // This is the key fix - telling the hook this is provider viewing mode
  );

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      setCustomerName("");
      setCustomerEmail("");
      setSelectedServiceId("");
      setSelectedDate(undefined);
      setSelectedTimeSlotId("");
      setEmailValidation({
        isValidating: false,
        isValid: null,
        existingUserId: null,
      });
    }
  }, [open]);

  // Reset time slot when service or date changes
  useEffect(() => {
    setSelectedTimeSlotId("");
  }, [selectedServiceId, selectedDate]);

  // Reset email validation when email changes
  useEffect(() => {
    setEmailValidation({
      isValidating: false,
      isValid: null,
      existingUserId: null,
    });
  }, [customerEmail]);

  const selectedService = services?.find(service => service.id === selectedServiceId);

  const validateEmail = async () => {
    if (!customerEmail) {
      toast({
        title: "Missing Email",
        description: "Please enter an email address to validate",
        variant: "destructive",
      });
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(customerEmail)) {
      toast({
        title: "Invalid Email",
        description: "Please enter a valid email address",
        variant: "destructive",
      });
      return;
    }

    setEmailValidation(prev => ({ ...prev, isValidating: true }));

    try {
      console.log('Validating email:', customerEmail);
      
      const { data: userId, error } = await supabase.rpc('find_user_by_email_for_booking', {
        p_email: customerEmail,
        p_provider_id: providerId
      });

      if (error) {
        console.error('Error validating email:', error);
        setEmailValidation({
          isValidating: false,
          isValid: false,
          existingUserId: null,
        });
        toast({
          title: "Validation Error",
          description: "Failed to validate email. Please try again.",
          variant: "destructive",
        });
        return;
      }

      const isValid = userId !== null;
      setEmailValidation({
        isValidating: false,
        isValid,
        existingUserId: userId,
      });

      if (isValid) {
        toast({
          title: "Email Validated",
          description: "Customer found! Booking will be linked to their existing account.",
        });
      } else {
        toast({
          title: "Customer Not Found",
          description: "No existing customer found with this email. Booking will be created with provider's account as fallback.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error in email validation:', error);
      setEmailValidation({
        isValidating: false,
        isValid: false,
        existingUserId: null,
      });
      toast({
        title: "Validation Error",
        description: "Failed to validate email. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleCreateBooking = async () => {
    if (!customerName || !customerEmail || !selectedServiceId || !selectedDate || !selectedTimeSlotId || !user) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    // Check if email has been validated
    if (emailValidation.isValid === null) {
      toast({
        title: "Email Not Validated",
        description: "Please validate the customer's email before creating the booking",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      console.log("Creating provider booking...");
      
      const selectedTimeSlotData = availableTimeSlots?.find(slot => slot.id === selectedTimeSlotId);
      
      if (!selectedTimeSlotData) {
        throw new Error("Selected time slot not found");
      }

      console.log("Provider booking - Time slot data before booking:", {
        slotId: selectedTimeSlotData.id,
        currentBookings: selectedTimeSlotData.current_bookings,
        maxBookings: selectedTimeSlotData.max_bookings,
        spotsLeft: selectedTimeSlotData.max_bookings - selectedTimeSlotData.current_bookings
      });

      // Calculate UTC booking times for consistent storage with time slots
      const { startTime: bookingStartTimeUTC, endTime: bookingEndTimeUTC } = calculateUTCBookingTimes(
        selectedTimeSlotData.start_time,
        selectedService?.duration_minutes || 60
      );
      
      // Get the booking date in local timezone format
      const localBookingDate = format(selectedDate, 'yyyy-MM-dd');

      console.log("Provider booking - Enhanced User ID assignment:", {
        providerId,
        serviceId: selectedServiceId,
        timeSlotId: selectedTimeSlotId,
        customerName: customerName,
        customerEmail: customerEmail,
        bookingDate: localBookingDate,
        startTime: bookingStartTimeUTC,
        endTime: bookingEndTimeUTC,
        expectedBookingCount: selectedTimeSlotData.current_bookings,
        existingCustomerId: emailValidation.existingUserId,
        willUseCustomerId: emailValidation.isValid ? true : false,
        fallbackUserId: user.id
      });

      // Use the atomic booking function with proper user ID assignment
      const { data: bookingResult, error: bookingError } = await supabase.rpc(
        'create_booking_with_slot_update',
        {
          p_user_id: emailValidation.existingUserId || user.id, // Use customer ID if found, otherwise provider ID as fallback
          p_provider_id: providerId,
          p_service_id: selectedServiceId,
          p_time_slot_id: selectedTimeSlotId,
          p_customer_name: customerName,
          p_customer_email: customerEmail,
          p_booking_date: localBookingDate,
          p_start_time: bookingStartTimeUTC,
          p_end_time: bookingEndTimeUTC,
          p_duration_minutes: selectedService?.duration_minutes || 60,
          p_price: 0, // Free booking created by provider
          p_currency: 'GBP',
          p_stripe_session_id: null,
          p_expected_booking_count: selectedTimeSlotData.current_bookings
        }
      );

      if (bookingError) {
        console.error('Atomic booking creation error:', bookingError);
        throw new Error(`Failed to create booking: ${bookingError.message}`);
      }

      // Add a note to the booking indicating it was created by the provider
      if (bookingResult && bookingResult.length > 0) {
        const bookingId = bookingResult[0].booking_id;
        const noteText = emailValidation.isValid 
          ? `Booking created by provider for existing customer account.`
          : `Booking created by provider. Customer will need to sign up with email ${customerEmail} to access this booking.`;
        
        await supabase
          .from('bookings')
          .update({ notes: noteText })
          .eq('id', bookingId);

        console.log('Added note to booking:', noteText);
        console.log('Final booking created with user_id:', emailValidation.existingUserId || user.id);
      }

      console.log('Provider booking created successfully:', bookingResult);

      const successMessage = emailValidation.isValid 
        ? `Booking created for ${customerName}! Customer will see this booking in their dashboard.`
        : `Booking created for ${customerName}! Customer will need to sign up with ${customerEmail} to access this booking.`;

      toast({
        title: "Booking Created!",
        description: successMessage,
      });
      
      onBookingCreated();
      onOpenChange(false);
    } catch (error) {
      console.error('Provider booking creation error:', error);
      toast({
        title: "Error",
        description: "Failed to create booking. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const getEmailValidationIcon = () => {
    if (emailValidation.isValidating) {
      return <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full" />;
    }
    if (emailValidation.isValid === true) {
      return <Check className="h-4 w-4 text-green-500" />;
    }
    if (emailValidation.isValid === false) {
      return <AlertCircle className="h-4 w-4 text-red-500" />;
    }
    return null;
  };

  const getEmailValidationMessage = () => {
    if (emailValidation.isValid === true) {
      return "Customer found - booking will be linked to existing account";
    }
    if (emailValidation.isValid === false) {
      return "Customer not found - booking will use provider account as fallback";
    }
    return null;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create New Booking</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Customer Information */}
          <div className="space-y-4">
            <h3 className="font-medium">Customer Information</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="customerName">Customer Name *</Label>
                <Input
                  id="customerName"
                  type="text"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  placeholder="Enter customer's full name"
                />
              </div>
              
              <div>
                <Label htmlFor="customerEmail">Email Address *</Label>
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <Input
                      id="customerEmail"
                      type="email"
                      value={customerEmail}
                      onChange={(e) => setCustomerEmail(e.target.value)}
                      placeholder="Enter customer's email"
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={validateEmail}
                      disabled={emailValidation.isValidating || !customerEmail}
                      className="shrink-0"
                    >
                      {getEmailValidationIcon()}
                      {emailValidation.isValidating ? "Validating..." : "Validate"}
                    </Button>
                  </div>
                  {getEmailValidationMessage() && (
                    <p className={`text-sm ${emailValidation.isValid ? 'text-green-600' : 'text-red-600'}`}>
                      {getEmailValidationMessage()}
                    </p>
                  )}
                  <p className="text-sm text-gray-600">
                    Email must exactly match the customer's registered email to link the booking
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Service Selection */}
          <div>
            <Label>Select Service *</Label>
            <Select value={selectedServiceId} onValueChange={setSelectedServiceId}>
              <SelectTrigger>
                <SelectValue placeholder="Choose a service" />
              </SelectTrigger>
              <SelectContent>
                {services?.map((service) => (
                  <SelectItem key={service.id} value={service.id}>
                    {service.name} - {service.duration_minutes}min - £{service.price}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Date Selection */}
          <div>
            <Label className="flex items-center space-x-2 mb-2">
              <CalendarIcon className="h-4 w-4" />
              <span>Select Date *</span>
            </Label>
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={setSelectedDate}
              disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
              className="rounded-md border"
            />
          </div>

          {/* Time Slot Selection */}
          {selectedDate && selectedServiceId && (
            <div>
              <Label className="flex items-center space-x-2 mb-2">
                <Clock className="h-4 w-4" />
                <span>Select Time Slot *</span>
              </Label>
              {timeSlotsLoading ? (
                <div className="animate-pulse">
                  <div className="grid grid-cols-3 gap-3">
                    {[1, 2, 3, 4, 5, 6].map((i) => (
                      <div key={i} className="h-12 bg-gray-200 rounded"></div>
                    ))}
                  </div>
                </div>
              ) : availableTimeSlots && availableTimeSlots.length > 0 ? (
                <div className="grid grid-cols-3 gap-3">
                  {availableTimeSlots.map((timeSlot) => {
                    const startTime = new Date(timeSlot.start_time);
                    const timeString = format(startTime, "HH:mm");
                    const isSelected = selectedTimeSlotId === timeSlot.id;
                    const spotsLeft = timeSlot.max_bookings - timeSlot.current_bookings;
                    
                    return (
                      <Button
                        key={timeSlot.id}
                        variant={isSelected ? "default" : "outline"}
                        onClick={() => setSelectedTimeSlotId(timeSlot.id)}
                        className="h-16 flex flex-col justify-center"
                        disabled={spotsLeft === 0}
                      >
                        <div className="font-medium">{timeString}</div>
                        <div className="text-xs opacity-70">
                          {spotsLeft} spot{spotsLeft !== 1 ? 's' : ''} left
                        </div>
                      </Button>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Clock className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No available time slots for this date</p>
                  <p className="text-sm mt-2">Please select a different date</p>
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-6">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateBooking}
              disabled={loading || !customerName || !customerEmail || !selectedServiceId || !selectedDate || !selectedTimeSlotId || emailValidation.isValid === null}
            >
              {loading ? "Creating..." : "Create Booking"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CreateBookingDialog;
